// Optimized Filter context with performance improvements and fixed entity accumulation logic
// This updated version adds support for an `interactionTypes` filter so that
// selecting interaction types in the CountryRelationsFilter component properly
// updates the query parameters. The initial filters state now includes
// `interactionTypes`, and the resetFilters function clears it along with
// the other country relations filters. No changes are required in the
// accumulation logic—interactionTypes behaves just like the other arrays.
import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import storageService from '../services_main/storageService';

export const FilterContext = createContext();

const createInitialFilters = (overrides = {}) => ({
  search: '',
  countries: [],
  people: [],
  organizations: [],
  regions: [],
  sectors: [],
  intensity: [],
  scope: [],
  startDate: '',
  endDate: '',
  // New country relations filters
  relationTypes: [],
  impactTypes: [],
  // Added: single interactionTypes array to support OR logic for interactions
  interactionTypes: [],
  primaryInteractionTypes: [],
  secondaryInteractionTypes: [],
  roles: [],
  countryMatchMode: 'any',
  ...overrides
});

const isEntityQueryActive = () => {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname !== '/') return false;
  try {
    const params = new URLSearchParams(window.location.search || '');
    return Boolean(
      params.get('country') ||
      params.get('organization') ||
      params.get('person') ||
      params.get('region')
    );
  } catch (err) {
    return false;
  }
};

export function FilterProvider({ children }) {
  // Initialize filters state
  const [filters, setFilters] = useState(() => createInitialFilters());

  const [sortParams, setSortParams] = useState({
    field: 'news_date',
    order: 'desc'
  });

  // Updated view modes: grid, list only
  const [viewMode, setViewMode] = useState('grid');

  // Selected entity for info display
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Flag to indicate if entity was loaded from URL
  const [entityFromUrl, setEntityFromUrl] = useState(false);

  // Track an active slug-based filter lock so slug visitors can't remove the locked filter
  const [slugFilterLock, setSlugFilterLock] = useState(null);

  const doesValueMatchSlugLock = useCallback((candidate, lock) => {
    if (!lock) return false;
    if (lock.matchBy === 'id') {
      const candidateId = candidate && typeof candidate === 'object' ? candidate.id : candidate;
      return candidateId === lock.valueId;
    }
    return candidate === lock.value;
  }, []);

  const enforceSlugLockValue = useCallback((name, incomingValue) => {
    if (!slugFilterLock || slugFilterLock.key !== name) {
      return incomingValue;
    }

    const lock = slugFilterLock;

    if (!Array.isArray(incomingValue)) {
      return [lock.value];
    }

    const filtered = incomingValue.filter((item, index, arr) => {
      if (!doesValueMatchSlugLock(item, lock)) {
        return true;
      }
      // Keep the first instance that matches the lock
      const firstIndex = arr.findIndex(candidate => doesValueMatchSlugLock(candidate, lock));
      return index === firstIndex;
    });

    const hasLock = filtered.some(item => doesValueMatchSlugLock(item, lock));

    if (!hasLock) {
      filtered.push(lock.value);
    } else if (lock.matchBy === 'id') {
      return filtered.map(item =>
        doesValueMatchSlugLock(item, lock) ? lock.value : item
      );
    }

    return lock.matchBy === 'primitive'
      ? Array.from(new Set(filtered))
      : filtered;
  }, [slugFilterLock, doesValueMatchSlugLock]);

  // Load filters from storage on mount
  useEffect(() => {
    const savedFilters = storageService.getItem('filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setFilters(createInitialFilters(parsed));
        }
      } catch {
        storageService.removeItem('filters');
      }
    }

    const savedSortParams = storageService.getItem('sortParams');
    if (savedSortParams) {
      try {
        const parsed = JSON.parse(savedSortParams);
        if (parsed && typeof parsed === 'object') setSortParams(parsed);
      } catch {
        storageService.removeItem('sortParams');
      }
    }

    const savedViewMode = storageService.getItem('viewMode');
    if (savedViewMode && ['grid', 'list'].includes(savedViewMode)) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Save filters to storage on change (debounced to avoid jank on rapid changes)
  useEffect(() => {
    if (!filters) return;
    const id = setTimeout(() => {
      try {
        storageService.setItem('filters', JSON.stringify(filters));
      } catch (e) {
        // ignore storage errors
      }
    }, 600);
    return () => clearTimeout(id);
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(() => {
      try {
        storageService.setItem('sortParams', JSON.stringify(sortParams));
      } catch (e) {
        // ignore storage errors
      }
    }, 400);
    return () => clearTimeout(id);
  }, [sortParams]);

  useEffect(() => {
    try {
      storageService.setItem('viewMode', viewMode);
    } catch (e) {
      // ignore storage errors
    }
  }, [viewMode]);

  // FIXED: Update a single filter with proper entity accumulation logic
  const updateFilter = useCallback((name, value, immediate = true) => {
    let validatedValue = Array.isArray(value) ? [...value] : value;

    validatedValue = enforceSlugLockValue(name, validatedValue);

    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: validatedValue
    }));

    // If on an entity URL page and an entity filter is being updated,
    // make sure we're also updating the entityFromUrl flag
    if (immediate && (name === 'countries' || name === 'people' || name === 'organizations' || name === 'sectors')) {
      if (isEntityQueryActive()) {
        // If the entity filter is being cleared on an entity URL page, something is wrong
        // Let's make sure we're tracking that this is a URL entity
        if (Array.isArray(value) ? value.length > 0 : !!value) {
          setEntityFromUrl(true);
        }
      }
    }
  }, [enforceSlugLockValue, slugFilterLock]);

  // FIXED: Add entity to filter (for accumulation on base URL)
  const addEntityToFilter = useCallback((entityType, entity) => {
    const isEntityUrl = isEntityQueryActive();

    // On entity URL pages, replace the entity filter (existing behavior)
    if (isEntityUrl) {
      updateFilter(entityType, [entity]);
      return;
    }

    // On base URL, accumulate entities (new behavior)
    setFilters(prevFilters => {
      const currentEntities = prevFilters[entityType] || [];
      // Check if entity already exists in the filter
      const entityExists = currentEntities.some(existingEntity => existingEntity.id === entity.id);
      if (entityExists) {
        return prevFilters;
      }
      const updatedEntities = enforceSlugLockValue(entityType, [...currentEntities, entity]);
      return {
        ...prevFilters,
        [entityType]: updatedEntities
      };
    });
  }, [updateFilter, enforceSlugLockValue]);

  // Updated resetFilters to clear the selected entity as well
  const resetFilters = useCallback((preserveUrlEntity = false, entityType = null, entity = null) => {
    setFilters(() => {
      const base = createInitialFilters();

      if (preserveUrlEntity && entityType && entity) {
        base[entityType] = [entity];
      }

      if (slugFilterLock) {
        const lockedValues = Array.isArray(slugFilterLock.lockedValues)
          ? slugFilterLock.lockedValues
          : [slugFilterLock.value];
        base[slugFilterLock.key] = enforceSlugLockValue(
          slugFilterLock.key,
          Array.isArray(base[slugFilterLock.key]) ? [...base[slugFilterLock.key]] : lockedValues
        );
      }

      return base;
    });
    // If not preserving URL entity, clear the entity selection state
    if (!preserveUrlEntity) {
      setSelectedEntity(null);
      setEntityFromUrl(false);
    }
  }, [slugFilterLock, enforceSlugLockValue]);

  const applySlugFilterLock = useCallback((lockConfig) => {
    if (!lockConfig || !lockConfig.key || lockConfig.value === undefined || lockConfig.value === null) {
      setSlugFilterLock(null);
      return;
    }

    const normalizedLock = {
      key: lockConfig.key,
      kind: lockConfig.kind || lockConfig.key,
      matchBy: lockConfig.matchBy || 'id',
      value: lockConfig.value,
      valueId: lockConfig.matchBy === 'id'
        ? (lockConfig.valueId !== undefined
          ? lockConfig.valueId
          : (lockConfig.value && typeof lockConfig.value === 'object' ? lockConfig.value.id : lockConfig.value))
        : undefined,
      displayLabel: lockConfig.displayLabel || '',
      slugPath: lockConfig.slugPath || '',
    };

    const lockedValues = Array.isArray(lockConfig.lockedValues)
      ? [...lockConfig.lockedValues]
      : [lockConfig.value];

    normalizedLock.lockedValues = lockedValues;

    setSlugFilterLock(normalizedLock);

    setFilters(() => {
      const base = createInitialFilters(lockConfig.additionalFilters || {});
      base[normalizedLock.key] = Array.isArray(lockedValues) ? [...lockedValues] : [lockedValues];
      return base;
    });

    setSelectedEntity(null);
    setEntityFromUrl(false);
  }, []);

  const clearSlugFilterLock = useCallback(() => {
    setSlugFilterLock(null);
  }, []);

  // Update sort parameters
  const updateSortParams = useCallback((field, order) => {
    setSortParams({ field, order });
  }, []);

  // Toggle between view modes: grid <-> list
  const toggleViewMode = useCallback(() => {
    setViewMode(prevMode => prevMode === 'grid' ? 'list' : 'grid');
  }, []);

  // Set specific view mode
  const setViewModeSpecific = useCallback((mode) => {
    if (['grid', 'list'].includes(mode)) {
      setViewMode(mode);
    }
  }, []);

  // FIXED: Entity selection functions with auto-scroll
  const selectEntity = useCallback((entity) => {
    setSelectedEntity(entity);
    setEntityFromUrl(false); // This is a manual selection, not from URL
    // Auto-scroll to top when entity is selected
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  }, []);

  const clearSelectedEntity = useCallback(() => {
    const isEntityUrl = isEntityQueryActive();
    // Only clear entityFromUrl if we're not on an entity URL page
    if (!isEntityUrl) {
      setEntityFromUrl(false);
    }
    // Always clear the selected entity
    setSelectedEntity(null);
  }, []);

  // Refined handler to cleanly set the entity after a reset
  const setEntityFromUrlHandler = useCallback((entity) => {
    setSelectedEntity(entity);
    setEntityFromUrl(true);
    // Determine the correct filter key
    const filterKey = entity.type === 'person'
      ? 'people'
      : entity.type === 'organization'
      ? 'organizations'
      : entity.type === 'country'
      ? 'countries'
      : entity.type === 'sector'
      ? 'sectors'
      : `${entity.type}s`;
    // This assumes filters have just been reset, so it sets this as the only filter
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterKey]: [entity]
    }));
  }, []);

  const contextValue = useMemo(() => ({
    filters,
    updateFilter,
    addEntityToFilter,
    resetFilters,
    sortParams,
    updateSortParams,
    viewMode,
    toggleViewMode,
    setViewModeSpecific,
    selectedEntity,
    selectEntity,
    clearSelectedEntity,
    entityFromUrl,
    setEntityFromUrl: setEntityFromUrlHandler,
    slugFilterLock,
    applySlugFilterLock,
    clearSlugFilterLock
  }), [
    filters,
    updateFilter,
    addEntityToFilter,
    resetFilters,
    sortParams,
    updateSortParams,
    viewMode,
    toggleViewMode,
    setViewModeSpecific,
    selectedEntity,
    selectEntity,
    clearSelectedEntity,
    entityFromUrl,
    setEntityFromUrlHandler,
    slugFilterLock,
    applySlugFilterLock,
    clearSlugFilterLock
  ]);

  return (
    <FilterContext.Provider
      value={contextValue}
    >
      {children}
    </FilterContext.Provider>
  );
}
